import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import {
    Box,
    Paper,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    Alert,
    Divider,
} from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import CheckIcon from '@mui/icons-material/Check'
import { datasetFormik } from './datasetFormik'
import { Formik, Form, useFormikContext } from 'formik'
import Textfield from '../../FormsUI/Textfield'
import Select from '../../FormsUI/Select'
import GeoJSONInput from '../../GeoJSONInput'
import Modal from '../../FormCreationModal'
import { createCanton, updateCanton, fetchCantonBoundary } from '../../../services/cantonService'

const STEPS = [
    { label: 'Información Básica', description: 'Datos del Cantón' },
    { label: 'Boundary GeoJSON', description: 'Límites geográficos' },
    { label: 'Configuración Avanzada', description: 'Ajustes opcionales' },
]

const gridStyle = {
    mt: 0,
    width: '100%',
    ml: 0,
    textAlignLast: 'right',
    justifyContent: 'space-evenly',
    pr: 2,
}

// Componente interno para el contenido del formulario
const FormContent = ({ formik, activeStep, setActiveStep, onCancel, onSubmit, initialValues }) => {
    const { values, errors, touched, setFieldValue, validateForm, setTouched } = useFormikContext()
    const [loadingBoundary, setLoadingBoundary] = React.useState(false)

    // Cargar Boundary si es edición y no hay geojson
    React.useEffect(() => {
        const loadBoundary = async () => {
            // Si estamos en modo edición (hay initialValues con slug) y no hay geojson aún
            if (initialValues?.slug && !values.boundary_geojson && !loadingBoundary) {
                setLoadingBoundary(true)
                const geojson = await fetchCantonBoundary(initialValues.slug)
                if (geojson) {
                    setFieldValue('boundary_geojson', JSON.stringify(geojson))
                }
                setLoadingBoundary(false)
            }
        }
        loadBoundary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues])


    const otherPropsControl = {
        InputProps: { readOnly: false },
        variant: 'outlined',
    }

    const renderField = (field, index) => {
        if (field.isGeoJSONInput) {
            return (
                <Grid item xs={12} key={`field-${index}`}>
                    <GeoJSONInput
                        value={values.boundary_geojson}
                        onChange={(val) => setFieldValue('boundary_geojson', val)}
                        error={touched.boundary_geojson && errors.boundary_geojson}
                    />
                </Grid>
            )
        }

        const child = !field.options ? (
            <Textfield
                {...otherPropsControl}
                {...field}
                type={field.type || 'text'}
            />
        ) : (
            <Select key={`field-${index}`} {...field} {...otherPropsControl} />
        )

        return (
            <Grid
                key={`field-${index}`}
                item
                xs={field.x}
                sx={{ textAlignLast: 'center' }}
            >
                {child}
            </Grid>
        )
    }

    const getStepFields = () => {
        switch (activeStep) {
            case 0:
                return formik.STEP1_FIELDS
            case 1:
                return formik.STEP2_FIELDS
            case 2:
                return formik.STEP3_FIELDS
            default:
                return []
        }
    }

    const getStepValidation = () => {
        switch (activeStep) {
            case 0:
                return formik.STEP1_VALIDATION
            case 1:
                return formik.STEP2_VALIDATION
            case 2:
                return formik.STEP3_VALIDATION
            default:
                return null
        }
    }

    const handleNext = async () => {
        const validation = getStepValidation()
        if (!validation) {
            setActiveStep(prev => prev + 1)
            return
        }

        // Validar solo los campos del paso actual
        const stepFields = getStepFields().map(f => f.name)
        const stepValues = {}
        stepFields.forEach(name => {
            stepValues[name] = values[name]
        })

        try {
            await validation.validate(stepValues, { abortEarly: false })
            setActiveStep(prev => prev + 1)
        } catch (err) {
            // Marcar campos como tocados para mostrar errores
            const touchedFields = {}
            stepFields.forEach(name => {
                touchedFields[name] = true
            })
            setTouched({ ...touched, ...touchedFields })

            // Validar el formulario completo para actualizar errores
            validateForm()
        }
    }

    const handleBack = () => {
        setActiveStep(prev => prev - 1)
    }

    const isLastStep = activeStep === STEPS.length - 1

    return (
        <Box>
            {/* Stepper Header */}
            <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
                {STEPS.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel>
                            <Typography variant="body2">{step.label}</Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Divider />

            {/* Step Description */}
            <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Paso {activeStep + 1}: {STEPS[activeStep].description}
                </Typography>
            </Box>

            <Divider />

            {/* Form Content */}
            <Paper
                style={{
                    overflow: 'auto',
                    borderRadius: 0,
                    maxHeight: '50vh',
                    minHeight: 300,
                }}
                sx={{ p: 2 }}
            >
                <Grid container spacing={2} sx={{ ...gridStyle }}>
                    {getStepFields().map((field, index) => renderField(field, index))}
                </Grid>

                {/* Mensaje informativo en paso 3 */}
                {activeStep === 2 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Estos campos son opcionales. Si ingresas un GeoJSON válido en el paso anterior y dejas estos campos vacíos,
                        se calcularán automáticamente.
                    </Alert>
                )}
            </Paper>

            <Divider />

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                <Button
                    onClick={onCancel}
                    color="inherit"
                >
                    Cancelar
                </Button>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        startIcon={<NavigateBeforeIcon />}
                    >
                        Atrás
                    </Button>

                    {isLastStep ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onSubmit}
                            startIcon={<CheckIcon />}
                        >
                            {initialValues ? 'Actualizar' : 'Crear'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={<NavigateNextIcon />}
                        >
                            Siguiente
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

const CantonFormModal = (props, ref) => {
    const dispatch = useDispatch()

    const { initialValues, onClose, onSuccess } = props
    const [formik, setFormik] = React.useState(null)
    const [open, setOpen] = React.useState(false)
    const [activeStep, setActiveStep] = React.useState(0)
    const formikRef = React.useRef(null)

    const handleCancelAction = () => {
        setOpen(false)
        setActiveStep(0)
        if (onClose) onClose()
    }

    const handleSubmitForm = async () => {
        if (!formikRef.current) return
        formikRef.current.submitForm()
    }

    const handleAddAction = async values => {
        let resultSave = { stored: true }

        // Construir objeto base
        const data = {
            nombre: values.nombre,
            idProvincia: parseInt(values.id_provincia, 10),
            habilitado: Boolean(parseInt(values.habilitado, 10)),
            slug: values.slug || undefined,
            boundary_geojson: values.boundary_geojson || undefined,
        }

        // Agregar campos opcionales solo si tienen valor
        if (values.center_lng !== '' && values.center_lng != null) {
            data.center_lng = parseFloat(values.center_lng)
        }
        if (values.center_lat !== '' && values.center_lat != null) {
            data.center_lat = parseFloat(values.center_lat)
        }
        if (values.zoom !== '' && values.zoom != null) {
            data.zoom = parseInt(values.zoom, 10)
        }
        if (values.bounds_sw_lng !== '' && values.bounds_sw_lng != null) {
            data.bounds_sw_lng = parseFloat(values.bounds_sw_lng)
        }
        if (values.bounds_sw_lat !== '' && values.bounds_sw_lat != null) {
            data.bounds_sw_lat = parseFloat(values.bounds_sw_lat)
        }
        if (values.bounds_ne_lng !== '' && values.bounds_ne_lng != null) {
            data.bounds_ne_lng = parseFloat(values.bounds_ne_lng)
        }
        if (values.bounds_ne_lat !== '' && values.bounds_ne_lat != null) {
            data.bounds_ne_lat = parseFloat(values.bounds_ne_lat)
        }

        let response
        if (initialValues?.id) {
            response = await updateCanton(initialValues.id, data)
        } else {
            response = await createCanton(data)
        }

        if (!response.success) {
            resultSave = { stored: false, message: response?.message || 'Error al guardar' }
        } else {
            if (onSuccess) onSuccess()
        }
        return resultSave
    }

    React.useImperativeHandle(ref, () => ({ openModal: () => setOpen(true) }))

    React.useEffect(() => {
        if (props.open !== undefined) setOpen(props.open)
    }, [props.open])


    React.useEffect(() => {
        if (!open) return
        datasetFormik().then(variables => {
            if (initialValues) {
                variables.INITIAL_FORMIK_STATE = {
                    ...variables.INITIAL_FORMIK_STATE,
                    nombre: initialValues.name || initialValues.nombre || '',
                    slug: initialValues.slug || '',
                    id_provincia: initialValues.idProvincia || initialValues.id_provincia || '',
                    habilitado: initialValues.enabled === true || initialValues.habilitado === true ? 1 : 0,
                    center_lng: initialValues.center?.[0] ?? '',
                    center_lat: initialValues.center?.[1] ?? '',
                    zoom: initialValues.zoom ?? '',
                    // geojson loaded via useEffect
                }
            }
            setFormik(variables)
        })
        return () => {
            setFormik(null)
            setActiveStep(0)
        }
    }, [open, initialValues])

    if (!Boolean(formik)) return null

    return (
        <div>
            <Modal
                open={open}
                title={initialValues ? "Editar Cantón" : "Crear un Cantón"}
                description=""
                maxWidth="md"
            >
                <Box>
                    <Formik
                        innerRef={formikRef}
                        initialValues={{ ...formik?.INITIAL_FORMIK_STATE }}
                        validationSchema={formik?.FORM_VALIDATION}
                        onSubmit={async values => {
                            const itsStored = await handleAddAction(values)
                            handleCancelAction()
                            dispatch(setIsStored(itsStored))
                        }}
                    >
                        <Form>
                            <FormContent
                                formik={formik}
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                onCancel={handleCancelAction}
                                onSubmit={handleSubmitForm}
                                initialValues={initialValues}
                            />
                        </Form>
                    </Formik>
                </Box>
            </Modal>
        </div>
    )
}

export default React.forwardRef(CantonFormModal)
